import { useCallback, useState } from "react";
import PageRecord from "models/view-models/page-record";
import useNotebooksAtom from "utilities/hooks/domain/notebooks/use-notebooks-atom";
import PageService, {
    PageRelocateBodyParams,
    PageRelocatePathParams,
} from "utilities/services/notebooks/page-service";

interface UseRelocatePageOptions {
    onSuccess?: (relocatedPage: PageRecord) => void;
    onError?: (errors: string[]) => void;
}

interface UseRelocatePage {
    relocatePage: (
        page: PageRecord,
        destinationNotebookId: number
    ) => Promise<PageRecord | null>;
    errors: string[];
    loading: boolean;
}

export default function useRelocatePage(
    options?: UseRelocatePageOptions
): UseRelocatePage {
    const { onSuccess, onError } = options || {};
    const { addPageToNotebook, removePageFromNotebook } = useNotebooksAtom();
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const { patch: relocatePageApi } = PageService.useRelocate();

    const relocatePage = useCallback(
        async (
            originalPage: PageRecord,
            destinationNotebookId: number
        ): Promise<PageRecord | null> => {
            setLoading(true);
            setErrors([]);

            try {
                if (!originalPage.id) {
                    throw new Error("Invalid page ID");
                }

                const pathParam: PageRelocatePathParams = {
                    id: originalPage.id,
                };
                const bodyParam: PageRelocateBodyParams = {
                    destinationNotebookId: destinationNotebookId,
                };

                const result = await relocatePageApi(pathParam, bodyParam);
                const relocatedPage = result.resultObject;

                if (result.result?.hasErrors() || !relocatedPage) {
                    const errorMessages = result.result?.errors?.map(
                        (error) => error.message || "Failed to relocate page"
                    ) || ["Failed to relocate page"];
                    setErrors(errorMessages);
                    onError?.(errorMessages);
                    return null;
                }

                removePageFromNotebook(
                    originalPage.id,
                    originalPage.notebookId
                );
                addPageToNotebook(relocatedPage.notebookId, relocatedPage);

                onSuccess?.(relocatedPage);
                return relocatedPage;
            } catch (error) {
                const errorMessage =
                    error instanceof Error
                        ? error.message
                        : "An unexpected error occurred while relocating the page";
                const errorMessages = [errorMessage];
                setErrors(errorMessages);
                onError?.(errorMessages);
                return null;
            } finally {
                setLoading(false);
            }
        },
        [
            relocatePageApi,
            removePageFromNotebook,
            addPageToNotebook,
            onSuccess,
            onError,
        ]
    );

    return {
        relocatePage,
        errors,
        loading,
    };
}
